// Putting the taster back in front of an unfinished tasting: the one tap behind every list of them.
// A tasting held here is brought forward; another device's copy is taken in; a flight or session the
// edge names opens through the screens that already join it. Each answers with the route to go to,
// which for a tasting under way is the pager at the page and wine it was left on.
import { EdgeApiError, endFlight, getFlight, leaveFlight } from "../edgeApi";
import { perform } from "../social/outbox";
import { endSession, getSession, leaveSession } from "../social/sessionsApi";
import { flushQueued } from "./actions";
import { resumePath } from "./run";
import { activateRun, adoptRun, discardRun, getAllRuns } from "./store";
import type { CloseTarget, ResumeTarget } from "./unfinished";

export function resumeRoute(target: ResumeTarget): string | undefined {
  switch (target.kind) {
    case "run": {
      const run = activateRun(target.runId);
      if (run === undefined) return undefined;
      // A note that waited for a connection goes out now if there is one.
      if (run.wines.some((wine) => wine.queued)) void flushQueued();
      return resumePath(run);
    }
    case "draft":
      adoptRun(target.snapshot, { active: true });
      return resumePath(target.snapshot);
    case "flight":
      return target.sessionId === null
        ? `/tasting/flight/${encodeURIComponent(target.code)}`
        : `/tasting/flight/${encodeURIComponent(target.code)}?session=${encodeURIComponent(target.sessionId)}`;
    default:
      return `/session/${encodeURIComponent(target.sessionId)}`;
  }
}

/**
 * Steps out of the shared tasting a copy belongs to, so it does not come back from the edge's lists
 * on this or any other device: the host ends it for everyone, anyone else is recorded as having
 * left. A tasting the edge no longer has (or already ended) counts as done; any other failure is
 * thrown, because discarding the copy anyway would only make the tasting reappear unexplained.
 */
async function stepOut(shared: {
  flightCode?: string | undefined;
  sessionId?: string | undefined;
}): Promise<void> {
  const { flightCode, sessionId } = shared;
  if (flightCode === undefined) return;
  try {
    if (sessionId === undefined) {
      await ((await getFlight(flightCode)).isHost ? endFlight : leaveFlight)(flightCode);
    } else {
      await ((await getSession(sessionId)).isHost ? endSession : leaveSession)(sessionId);
    }
  } catch (err) {
    if (err instanceof EdgeApiError && (err.status === 404 || err.status === 409)) return;
    throw err;
  }
}

/**
 * Throws an unfinished tasting away: a copy held here goes from this device (and so, once the sync
 * notices, from the edge); another device's copy that was never taken in is removed from the edge
 * through the outbox. If it is a shared tasting the person steps out of it first (see `stepOut`). A
 * flight or session without a copy here is closed with {@link closeUnfinished}.
 */
export async function discardUnfinished(
  target: ResumeTarget,
  userId: string | undefined,
): Promise<void> {
  if (target.kind === "run") {
    await stepOut(getAllRuns().find((run) => run.id === target.runId) ?? {});
    discardRun(target.runId);
    return;
  }
  if (target.kind === "draft" && userId !== undefined) {
    await stepOut(target.snapshot);
    discardRun(target.snapshot.id);
    await perform(userId, { kind: "draft_delete", runId: target.snapshot.id });
  }
}

/**
 * Closes a shared tasting: the host ends it for everyone, anyone else leaves it and the rest carry
 * on. It is not queued (an end or a leave that silently waits would leave the row on screen), so a
 * failure is thrown for the caller to show.
 */
export async function closeUnfinished(closing: CloseTarget): Promise<void> {
  switch (closing.kind) {
    case "end_session":
      await endSession(closing.sessionId);
      return;
    case "leave_session":
      await leaveSession(closing.sessionId);
      return;
    case "end_flight":
      await endFlight(closing.code);
      return;
    default:
      await leaveFlight(closing.code);
  }
}
